import jsPDF from 'jspdf';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import 'jspdf-autotable';

import {
  ReportOptions,
  ReportRow,
  ReportTableOptions,
} from './report-generator.model';
import { convertPngToBase64, convertSvgToBase64 } from './image-utils';

export class ReportGenerator {
  readonly pdf: jsPDF;
  readonly options: ReportOptions;
  verticalOffset: number;

  constructor(customOptions?: Partial<ReportOptions>) {
    this.pdf = new jsPDF();

    this.options = {
      fontFamily: 'helvetica',

      // Title defaults
      titleFontSize: 12,
      titleX: 105,
      titleY: 25,
      titleColor: [73, 83, 85],
      titleAlign: 'center',
      subtitleFontSize: 10,
      subtitleX: 105,
      subtitleY: 30,
      subtitleColor: [73, 83, 85],
      subtitleAlign: 'center',

      // Composite Section defaults
      compositeSectionFontSize: 10,
      compositeSectionColor: [3, 159, 206],
      compositeSectionFontColor: [255, 255, 255],
      compositeSectionMargin: 0,
      compositeSectionPadding: 5,
      compositeSectionFontStyle: 'bold',
      compositeSectionAlign: 'left',

      // Add Photo Section Header defaults
      photoSectionHeaderBackgroundColor: [235, 236, 237],
      photoSectionHeaderHeight: 10,
      photoSectionHeaderWidth: 190,
      photoSectionHeaderMargin: 5,
      photoSectionHeaderPadding: 7,
      photoSectionHeaderFontSize: 8,
      photoSectionHeaderFontColor: [73, 83, 85],
      photoSectionHeaderIconWidth: 6,
      photoSectionHeaderIconHeight: 6,

      // Header defaults for autotable
      headerColorAutoTable: [220, 247, 255],
      headerFontColorAutoTable: [73, 83, 85],
      headerFontSizeAutoTable: 10,
      headerAlignAutoTable: 'left',
      startY: 30,
      head: [],
      body: [],
      margin: { left: 25, right: 25 },
      tableWidthAutoTable: 190,
      styles: {
        font: 'helvetica',
        fontSize: 8,
        textColor: [73, 83, 85],
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: [3, 159, 206],
        textColor: [255, 255, 255],
        fontSize: 10,
        halign: 'center',
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [73, 83, 85],
        fontSize: 8,
      },
      theme: 'striped',
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },

      // Signature options defaults
      signatureHeight: 10,
      signatureWidth: 20,
      signatureFontSize: 8,
      signatureFontColor: [73, 83, 85],
      signatureAlign: 'right',
      signatureGap: 5,

      // Header defaults
      headerColor: [73, 83, 85],
      headerFontColor: [255, 255, 255],
      headerFontSize: 10,
      headerX: 105,
      headerY: 35,
      headerAlign: 'center',

      // Section row defaults
      sectionRowColor: [235, 236, 237],
      sectionRowFontColor: [73, 83, 85],
      sectionRowFontSize: 8,

      // Row defaults
      rowFontSize: 8,
      rowBorderColor: [235, 236, 237],

      // Layout defaults
      paddingLeft: 2,
      horizontalOffset: 10,
      rowHeight: 5,
      tableWidth: 190,
      headerMargin: 2,
      tablesGap: 5,

      // Pagination defaults
      paginationX: 190,
      paginationY: 290,

      // Logo defaults
      logoX: 10,
      logoY: 10,
      logoWidth: 50,
      logoHeight: 20,

      // Image defaults
      imageWidth: 90,
      imageHeight: 90,
      imageX: 10,
      imageY: 0,
      imageFormat: 'PNG',
      maxImagesPerRow: 3,
      gapBetweenImages: 10,

      // Safe area
      safeVerticalOffset: 30,

      ...customOptions,
    };

    this.verticalOffset = this.options.safeVerticalOffset;

    this.pdf.setFont(this.options.fontFamily);
    this.pdf.setTextColor(...this.options.headerFontColor);
  }

  addTitle(title: string, subtitle?: string) {
    this.pdf.setFontSize(this.options.titleFontSize);
    this.pdf.setTextColor(...this.options.titleColor);
    this.pdf.text(title, this.options.titleX, this.options.titleY, {
      align: this.options.titleAlign,
    });
    this.verticalOffset += (this.options.titleFontSize) / 2;

    if (subtitle) {
      this.addSubtitle(subtitle);
      this.verticalOffset += (this.options.subtitleFontSize) / 2;
    }
  }

  addGap(gap?: number) {
    this.verticalOffset += gap || this.options.tablesGap;
  }

  /**
   * this method implement some shortcuts, like:
   * __b__ for bold -> __b__this is bold text__b__
   * __i__ for italic -> __i__this is italic text__i__
   *
   * all in one line
   */
  addTextLine(line: string, fontSize?: number) {
    const boldRegex = /__b__(.*?)__b__/g;
    const italicRegex = /__i__(.*?)__i__/g;

    const boldMatches = boldRegex.test(line);
    const italicMatches = italicRegex.test(line);

    const effectiveFontSize = fontSize ?? this.options.subtitleFontSize;
    this.pdf.setFontSize(effectiveFontSize);
    this.pdf.setTextColor(...this.options.subtitleColor);

    let position = this.options.horizontalOffset;

    if (boldMatches) {
      const boldLines = line.split(boldRegex);
      boldLines.forEach((boldLine, index) => {
        if (index % 2 === 0) {
          this.pdf.setFont(this.options.fontFamily, 'normal');
        } else {
          this.pdf.setFont(this.options.fontFamily, 'bold');
        }
        this.pdf.text(boldLine, position, this.verticalOffset);
        position += this.pdf.getTextWidth(boldLine);
      });
    } else if (italicMatches) {
      const italicLines = line.split(italicRegex);
      italicLines.forEach((italicLine, index) => {
        if (index % 2 === 0) {
          this.pdf.setFont(this.options.fontFamily, 'normal');
        } else {
          this.pdf.setFont(this.options.fontFamily, 'italic');
        }
        this.pdf.text(italicLine, position, this.verticalOffset);
        position += this.pdf.getTextWidth(italicLine);
      });
    } else {
      this.pdf.setFont(this.options.fontFamily, 'normal');
      this.pdf.text(line, position, this.verticalOffset);
    }
    this.verticalOffset += effectiveFontSize / 2;
  }

  addSubtitle(subtitle: string) {
    this.pdf.setFontSize(this.options.subtitleFontSize);
    this.pdf.setTextColor(...this.options.subtitleColor);
    this.pdf.text(subtitle, this.options.subtitleX, this.options.subtitleY, {
      align: this.options.subtitleAlign,
    });
  }

  addAttributes(rows: ReportRow[], tableOptions: ReportTableOptions) {
    const totalPercentage = tableOptions.columns.reduce(
      (sum, col) => sum + col.percentageWidth,
      0,
    );
    if (totalPercentage > 100) {
      return;
    }
    const colWidths = tableOptions.columns.map(
      (col) => (col.percentageWidth / 100) * this.options.tableWidth,
    );

    this.verticalOffset += this.options.tablesGap;
    this.drawTableHeader(tableOptions, colWidths);
    this.verticalOffset += this.options.rowHeight + this.options.headerMargin;
    this.drawTableRows(rows, tableOptions, colWidths);
    this.verticalOffset += this.options.tablesGap;
  }

  private drawTableHeader(
    tableOptions: ReportTableOptions,
    colWidths: number[],
  ) {
    this.pdf.setFillColor(...this.options.headerColor);
    this.pdf.setTextColor(...this.options.headerFontColor);
    this.pdf.setFontSize(this.options.headerFontSize);
    this.pdf.rect(
      this.options.horizontalOffset,
      this.verticalOffset,
      this.options.tableWidth,
      this.options.rowHeight,
      'F',
    );

    const verticalCenter = this.options.rowHeight / 2;
    const verticalTextOffset =
      this.verticalOffset + verticalCenter + this.options.headerFontSize / 9;

    let xOffset = this.options.horizontalOffset + this.options.paddingLeft;
    tableOptions.columns.forEach((col, index) => {
      this.pdf.text(col.name, xOffset, verticalTextOffset);
      xOffset += colWidths[index];
    });
  }

  private drawTableRows(
    rows: ReportRow[],
    _tableOptions: ReportTableOptions,
    colWidths: number[],
  ) {
    let y = this.verticalOffset;
    const verticalCenter = this.options.rowHeight / 2;

    this.pdf.setTextColor(...this.options.sectionRowFontColor);
    this.pdf.setFontSize(this.options.rowFontSize);

    rows.forEach((row) => {
      if (this.checkPageLimit()) {
        y = this.verticalOffset;
      }

      const currentFontSize = row.isSectionRow
        ? this.options.sectionRowFontSize
        : this.options.rowFontSize;
      const verticalTextOffset = y + verticalCenter + currentFontSize / 9;
      this.pdf.setFont(
        this.options.fontFamily,
        row.isSectionRow ? 'bold' : 'normal',
      );

      if (row.isSectionRow) {
        this.pdf.setFontSize(this.options.sectionRowFontSize);
        this.pdf.setFillColor(...this.options.sectionRowColor);
        this.pdf.rect(
          this.options.horizontalOffset,
          y,
          colWidths.reduce((a, b) => a + b, 0),
          this.options.rowHeight,
          'F',
        );
      } else {
        this.pdf.setDrawColor(...this.options.rowBorderColor);
        let _xOffset = this.options.horizontalOffset;
        for (let i = 1; i < colWidths.length; i++) {
          _xOffset += colWidths[i - 1];
          this.pdf.line(_xOffset, y, _xOffset, y + this.options.rowHeight);
        }
      }

      let xOffset = this.options.horizontalOffset + this.options.paddingLeft;
      row.value.forEach((cell, index) => {
        this.pdf.text(`${cell}`, xOffset, verticalTextOffset);
        xOffset += colWidths[index];
      });

      if (!row.isSectionRow) {
        this.pdf.line(
          this.options.horizontalOffset,
          y + this.options.rowHeight,
          this.options.horizontalOffset + colWidths.reduce((a, b) => a + b, 0),
          y + this.options.rowHeight,
        );
      }

      y += this.options.rowHeight;
      if (row.isSectionRow) {
        this.pdf.setFontSize(this.options.rowFontSize);
      }

      this.verticalOffset = y;
    });
  }

  private checkPageLimit(): boolean {
    const pageHeight = this.pdf.internal.pageSize.height;
    if (
      this.verticalOffset + this.options.rowHeight >
      pageHeight - this.options.safeVerticalOffset
    ) {
      this.addNewPage();
      return true;
    }
    return false;
  }

  addNewPage() {
    this.pdf.addPage();
    this.verticalOffset = this.options.safeVerticalOffset;
  }

  async addLogo(imgPath: string) {
    try {
      const isSvg = imgPath.endsWith('.svg');
      const base64Png = await (isSvg
        ? convertSvgToBase64(imgPath)
        : convertPngToBase64(imgPath));
      this.pdf.addImage(
        base64Png,
        'PNG',
        this.options.logoX,
        this.options.logoY,
        this.options.logoWidth,
        this.options.logoHeight,
      );
    } catch (_) {
      // Logo loading failed — continue without logo
    }
  }

  addAutoTable<T>(
    columns: { header: string; dataKey: keyof T }[],
    data: T[],
    customOptions?: Partial<T>,
  ) {
    const pageWidth = this.pdf.internal.pageSize.width;
    const tableWidth = this.options.tableWidthAutoTable;
    const leftMargin = (pageWidth - tableWidth) / 2;

    const defaultOptions: Partial<ReportOptions> = {
      startY: this.verticalOffset || this.options.startY,
      head: [columns.map((col) => col.header)],
      body: data.map((row) => columns.map((col) => `${row[col.dataKey] || ''}`)),
      margin: { left: leftMargin, right: leftMargin },
      tableWidth,
      theme: this.options.theme,
      styles: {
        font: this.options.fontFamily,
        fontSize: this.options.rowFontSize,
        textColor: this.options.styles.textColor,
        fillColor: this.options.styles.fillColor,
      },
      headStyles: {
        fillColor: this.options.headerColorAutoTable,
        textColor: this.options.headerFontColorAutoTable,
        fontSize: this.options.headerFontSizeAutoTable,
        halign: this.options.headerAlignAutoTable as 'center' | 'left' | 'right',
      },
      bodyStyles: {
        fillColor: this.options.sectionRowColor,
        textColor: this.options.sectionRowFontColor,
        fontSize: this.options.rowFontSize,
      },
    };

    (this.pdf as any).autoTable({ ...defaultOptions, ...customOptions });

    this.verticalOffset = (this.pdf as any).lastAutoTable.finalY + this.options.tablesGap;
  }

  addPagination() {
    const pageCount = this.pdf.getNumberOfPages();
    this.pdf.setTextColor(...this.options.sectionRowFontColor);
    for (let i = 1; i <= pageCount; i++) {
      this.pdf.setPage(i);
      this.pdf.text(`${i}`, this.options.paginationX, this.options.paginationY);
    }
  }

  async save(fileName: string) {
    this.addPagination();

    if (Capacitor.getPlatform() === 'web') {
      this.pdf.save(fileName);
      return;
    } else {
      const pdfBase64String = this.pdf.output('datauristring').split(',')[1];

      const check = await Filesystem.checkPermissions();
      if (check.publicStorage !== 'granted') {
        await Filesystem.requestPermissions();
      }

      const dir = await Filesystem.getUri({
        directory: Directory.Documents,
        path: '',
      });
      const fullPath = `${dir.uri}/${fileName}`;

      await Filesystem.writeFile({
        path: fullPath,
        data: pdfBase64String,
      });

      await FileOpener.open({
        filePath: fullPath,
        contentType: 'application/pdf',
      });
    }
  }
}
